const REGULATION_QUARTERS = [1, 2, 3, 4];

// Play nodes carry the score ENTERING the play, so a quarter's closing score is
// the pre-play score of the next quarter's first play. The last quarter that
// has plays closes on the game's final score. Using the quarter's own highest
// pre-play score instead would drop points scored on its final play.
export const buildQuarterScores = (plays, game) => {
  if (!plays?.length) return [];

  const sorted = [...plays].sort(
    (a, b) => (a.gameIndex ?? 0) - (b.gameIndex ?? 0),
  );

  const presentQuarters = [
    ...new Set(
      plays.map((p) => p.quarter).filter((q) => typeof q === 'number'),
    ),
  ].sort((a, b) => a - b);

  const orderedQuarters = [
    ...REGULATION_QUARTERS,
    ...presentQuarters.filter((q) => q > 4),
  ];

  const firstPlayOf = (quarter) => sorted.find((p) => p.quarter === quarter);
  const lastPlayOf = (quarter) =>
    [...sorted].reverse().find((p) => p.quarter === quarter);

  let previousHome = 0;
  let previousAway = 0;

  return orderedQuarters.map((quarter, index) => {
    if (!firstPlayOf(quarter)) {
      return { quarter, home: 0, away: 0 };
    }

    const nextQuarter = orderedQuarters
      .slice(index + 1)
      .find((q) => firstPlayOf(q));
    const nextFirstPlay = nextQuarter ? firstPlayOf(nextQuarter) : null;
    const lastPlay = lastPlayOf(quarter);

    const endHome =
      nextFirstPlay?.homeScore ??
      game?.homeScore ??
      lastPlay?.homeScore ??
      previousHome;
    const endAway =
      nextFirstPlay?.awayScore ??
      game?.awayScore ??
      lastPlay?.awayScore ??
      previousAway;

    const home = endHome - previousHome;
    const away = endAway - previousAway;

    previousHome = endHome;
    previousAway = endAway;

    return { quarter, home, away };
  });
};

export const getQuarterLabel = (quarter) =>
  quarter > 4 ? `OT${quarter - 4 > 1 ? quarter - 4 : ''}` : `Q${quarter}`;

// The API reports every overtime play as quarter 5, so the overtime period is
// not in the data. Overtime is played in pairs of possessions, one per team,
// so the period is inferred by counting overtime drives in order, two to a
// period. Returns a Map of play id -> 1-based overtime period; plays without a
// drive are left out.
export const getOvertimePeriods = (plays) => {
  const driveOrder = [];
  const driveOfPlay = new Map();

  [...(plays ?? [])]
    .filter((play) => play.quarter > 4 && play.drive?.gameIndex != null)
    .sort((a, b) => (a.gameIndex ?? 0) - (b.gameIndex ?? 0))
    .forEach((play) => {
      const driveIndex = play.drive.gameIndex;
      if (!driveOrder.includes(driveIndex)) driveOrder.push(driveIndex);
      driveOfPlay.set(play.id, driveIndex);
    });

  const periods = new Map();
  driveOfPlay.forEach((driveIndex, playId) => {
    periods.set(playId, Math.floor(driveOrder.indexOf(driveIndex) / 2) + 1);
  });
  return periods;
};
